import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { Medication } from './medicationsSlice'

interface MedicationListState {
  medications: Medication[]
  loading: boolean
  error: string | null
}

const initialState: MedicationListState = {
  medications: [],
  loading: false,
  error: null,
}

export const fetchMedications = createAsyncThunk('medicationList/fetchAll', async () => {
  const res = await fetch('/.netlify/functions/medications')
  if (!res.ok) throw new Error('Failed to fetch medications')
  return res.json() as Promise<Medication[]>
})

export const createMedication = createAsyncThunk(
  'medicationList/create',
  async (data: {
    name: string
    category: string
    form: string
    concentration: string
    default_dose_rate: number | null
    common_frequency: string
    stock_quantity: number
    reorder_threshold: number
    controlled_substance: boolean
    dea_schedule: string | null
    known_interactions: string
    contraindicated_species: string
    notes: string
  }) => {
    const res = await fetch('/.netlify/functions/medications', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to create medication')
    return res.json() as Promise<Medication>
  }
)

const medicationListSlice = createSlice({
  name: 'medicationList',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMedications.pending, (state) => {
        if (state.medications.length === 0) state.loading = true
      })
      .addCase(fetchMedications.fulfilled, (state, action) => {
        state.medications = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(fetchMedications.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to load'
      })
      .addCase(createMedication.fulfilled, (state, action) => {
        state.medications.push(action.payload)
        state.medications.sort((a, b) => a.name.localeCompare(b.name))
      })
  },
})

export default medicationListSlice.reducer
